import { Expo, TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { AnimatedSprite, SpriteAnimation } from "html-living-framework";
import { Application } from "../Application";
import * as Constants from "../Data/Constants";
import { Culture } from "../Data/Culture";
import * as SFX from "../Data/SoundEffect";
import * as Topics from "../Data/Topics";
import * as VO from "../Data/VoiceOver";
import Gameplay from "../Gameplay";
import { Button } from "./Button";
import Flag from "./Flag";

const FPS = 12;
const Y_POSITION_VISIBLE = 80;
const Y_POSITION_HIDDEN = 500;
const SCALE = 0.8;

const FLAG_X_VISIBLE = -90;
const FLAG_X_HIDDEN = 0;

export default class Globe extends Button {
    public get name() {
        return "Globe";
    }
    protected _subscriptions: any[];
    protected _animatedSprite: AnimatedSprite;
    protected _queuedStop?: string;
    protected readonly _startingCulture: Culture = Gameplay.Instance.currentCulture;
    protected _introTimeline?: TimelineLite;
    protected readonly _flag: Flag;

    constructor() {
        super(() => this.onCultureButtonPressed(), "globe-bg", "globe", VO.U15);

        const loop = new SpriteAnimation("loop", "globe", 7, 4, FPS, true, 1);
        loop.onLoop(this.onLoop.bind(this));
        const mexico = new SpriteAnimation("mexico-stop", "globe", 7, 4, FPS, false, 1);
        const india = new SpriteAnimation("india-stop", "globe", 7, 4, FPS, false, 1);
        const egypt = new SpriteAnimation("egypt-stop", "globe", 7, 4, FPS, false, 1);

        this._animatedSprite = new AnimatedSprite();
        this._animatedSprite.addAnimation("loop", loop);
        this._animatedSprite.addAnimation("stop0", mexico);
        this._animatedSprite.addAnimation("stop1", india);
        this._animatedSprite.addAnimation("stop2", egypt);

        this._visuals!.addChild(this._animatedSprite);
        this._animatedSprite.position.set(4, -10); // normally I wouldn't do sub-pixel positioning

        this._animatedSprite.play("loop");

        this.changeCultureButtonInputState(false);

        this._subscriptions = new Array<any>();
        this._subscriptions.push(
            PubSub.subscribe(Topics.CULTURE_CHANGE_COMPLETE, this.onCultureChangeComplete.bind(this)),
            PubSub.subscribe(Topics.GAME_PAUSED, this.onPause.bind(this)),
            PubSub.subscribe(Topics.GAME_UNPAUSED, this.onResume.bind(this)),
            );

        this.position.y = Y_POSITION_HIDDEN;
        this.scale.set(SCALE);

        this._flag = this.addChild(new Flag());
        this._flag.scale.set(1 / SCALE, 1 / SCALE);
        this._flag.anchor.set(0.5);
        this._flag.position.x = FLAG_X_HIDDEN;
        this.setChildIndex(this._flag, 0);
    }

    public destroy(pOptions?: boolean | PIXI.DestroyOptions) {
        this._subscriptions.forEach(PubSub.unsubscribe);
        this._introTimeline?.kill();
        super.destroy(pOptions);
    }

    public changeCultureButtonInputState(pEnabled: boolean, pSendKeyboardUpdate: boolean = false): void {
        this.interactive = pEnabled;
        if (pSendKeyboardUpdate) {
            PubSub.publishSync(Topics.KEYBOARD_FOCUSABLE_INTERACTIVE_CHANGED, this);
        }
    }

    public startIntro() {
        let lastUpdate = new Date().getTime();
        this._introTimeline = new TimelineLite();
        this._introTimeline
            .fromTo(
                this,
                1,
                {
                    pixi : {
                        positionY: Y_POSITION_HIDDEN,
                        scaleX: 0,
                        scaleY: 0,
                    },
                },
                {
                    pixi : {
                        positionY: Y_POSITION_VISIBLE,
                        scaleX: SCALE,
                        scaleY: SCALE,
                    },
                    ease: Expo.easeOut,
                },
                1,
            )
            .to(this._flag, 0.25, {
                pixi: {
                    positionX: FLAG_X_VISIBLE - 100,
                },
            }, 2)
            .call(() => {
                this.setChildIndex(this._flag, 1); // put flag in front of globe
            })
            .to(this._flag, 0.25, {
                pixi: {
                    positionX: FLAG_X_VISIBLE,
                },
            })
            .call(() => {
                this.queueStop(this._startingCulture);
                PubSub.publishSync(HLF.REGISTER_FOCUSABLE, this);
                this._introTimeline = undefined;
            })
            .eventCallback(Constants.TWEEN_ONUPDATE, () => {
                const now = new Date().getTime();
                const deltaTime = (now - lastUpdate) / 1000;
                this._animatedSprite.update(deltaTime);
                lastUpdate = now;
            })
        ;
    }

    public update(pDeltaTime: number) {
        this._animatedSprite.update(pDeltaTime);
    }

    public getOnboardingRotation() {
        return PIXI.DEG_TO_RAD * 270;
    }

    public getOnboardingPosition() {
        return this.toGlobal(new PIXI.Point(-this.width / 2, 0));
    }

    protected onLoop() {
        if (this._queuedStop) {
            this._animatedSprite.play(this._queuedStop);
            this._queuedStop = undefined;
        }
    }

    protected onCultureButtonPressed(): void {
        this.changeCultureButtonInputState(false, true);
        this._animatedSprite.play("loop");
        PubSub.publishSync(Topics.CULTURE_BUTTON_PRESSED, this);
        Application.instance.audio.play(SFX.GLOBE_CLICKED, 1, false, HLF.AudioCategory.SFX);
        Application.instance.analytics.event("globe", "click");
        new TimelineLite().to(this._flag, 0.25, {
            pixi: {
                positionX: FLAG_X_VISIBLE - 100,
            },
        })
        .call(() => {
            this.setChildIndex(this._flag, 0); // put flag behind globe
        })
        .to(this._flag, 0.25, {
            pixi: {
                positionX: FLAG_X_HIDDEN,
            },
        });
    }

    protected onCultureChangeComplete(pTopic: string, pData: Culture) {
        this.queueStop(pData);
        // this.changeCultureButtonInputState(true, true);
        new TimelineLite().to(this._flag, 0.25, {
            pixi: {
                positionX: (-this.width / 2) - 100,
            },
        })
        .call(() => {
            this.setChildIndex(this._flag, 1); // put flag in front of globe
        })
        .to(this._flag, 0.25, {
            pixi: {
                positionX: (-this.width / 2),
            },
        });
    }

    protected queueStop(pCulture: Culture) {
        this._queuedStop = "stop" + pCulture.toString();
    }
    protected onPause(pTopic: string) {
        if (this._introTimeline !== undefined) {
            this._introTimeline.pause();
        }
    }
    protected onResume(pTopic: string) {
        if (this._introTimeline !== undefined) {
            this._introTimeline.resume();
        }
    }
}
